import { ComponentType, createContext, ReactNode, useContext } from 'react'
import type { ReduxStore } from './redux-store'
import { type AppState, type MessageOf, type ProcessHandle, type ProcessMap } from './process'

type StoreProviderProps<P extends ProcessMap> = {
  store: ReduxStore<P>
  children?: ReactNode
}

type StoreProviderApi<P extends ProcessMap> = {
  StoreProvider: ComponentType<StoreProviderProps<P>>
  /**
   * Subscribe with static, preferably module-scope selectors. Zustand compares
   * selected values with deepEqual to decide whether React re-renders.
   *
   * The three-argument form only passes an equality-safe argument to the
   * selector; it does not add parameterized memoization. Keep such selectors
   * cheap, and introduce a keyed cache only when a measured caller needs one.
   */
  useSelect: {
    <T>(selector: (root: AppState<P>) => T): T
    <K extends keyof AppState<P>>(key: K): AppState<P>[K]
    <K extends keyof AppState<P>, T>(key: K, selector: (slice: AppState<P>[K]) => T): T
    <K extends keyof AppState<P>, A, T>(
      key: K,
      selector: (slice: AppState<P>[K], arg: A) => T,
      arg: A
    ): T
  }
  useProcess: <K extends keyof P & string>(key: K) => ProcessHandle<MessageOf<P[K]>>
}

function makeStoreProvider<const P extends ProcessMap>(): StoreProviderApi<P> {
  const StoreContext = createContext<ReduxStore<P> | undefined>(undefined)

  function StoreProvider({ children, store }: StoreProviderProps<P>) {
    return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
  }

  function useStore(): ReduxStore<P> {
    return useContext(StoreContext)!
  }

  function useSelect<T>(selector: (root: AppState<P>) => T): T
  function useSelect<K extends keyof AppState<P>>(key: K): AppState<P>[K]
  function useSelect<K extends keyof AppState<P>, T>(
    key: K,
    selector: (slice: AppState<P>[K]) => T
  ): T
  function useSelect<K extends keyof AppState<P>, A, T>(
    key: K,
    selector: (slice: AppState<P>[K], arg: A) => T,
    arg: A
  ): T
  function useSelect<K extends keyof AppState<P>, A, T>(
    arg1: K | ((root: AppState<P>) => T),
    arg2?: (slice: AppState<P>[K], arg: A) => T,
    arg3?: A
  ) {
    const store = useStore()

    if (typeof arg1 == 'function') {
      return store.useSelect(arg1)
    }

    return store.useSelect((rootState) => {
      const processState = rootState[arg1]
      if (processState == undefined) {
        throw new Error(`Process ${String(arg1)} is not required at boot`)
      }

      if (arg2 == undefined) return processState
      return arg2(processState, arg3!)
    })
  }

  function useProcess<K extends keyof P & string>(key: K): ProcessHandle<MessageOf<P[K]>> {
    const store = useStore()
    const storeState = store.getState()

    if (storeState[key] == undefined) {
      throw new Error(`Process ${String(key)} is not required at boot`)
    }

    return store.getProcessHandle(key)
  }

  return {
    StoreProvider,
    useSelect,
    useProcess
  }
}

export function createStoreProvider<const P extends ProcessMap>(): StoreProviderApi<P> {
  if (import.meta.hot) {
    const prev = import.meta.hot.data.storeProvider as StoreProviderApi<P> | undefined
    if (prev) {
      return prev
    }

    // Fast Refresh can re-run app store modules while mounted components still
    // read from the old provider context, so keep the provider tuple stable.
    const next = makeStoreProvider<P>()
    import.meta.hot.data.storeProvider = next
    return next
  }

  return makeStoreProvider<P>()
}
